import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { apiError } from '@/lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { value: 'performance_team', label: 'Rendimiento Equipo (hasta 11 atletas)' },
  { value: 'coach_pro', label: 'Coach Pro' },
  { value: 'coach_basic', label: 'Coach Básico' },
  { value: 'coach_elite', label: 'Coach Elite' },
  { value: 'salud_familiar', label: 'Salud Familiar' },
  { value: 'other', label: 'Aún no lo sé' },
];
const SIZES = ['1-5', '6-10', '11-20', '21-50', 'Más de 50'];

/** Solicitud de demo (equipos y coaches): formulario propio; el equipo ONOR contacta en menos de un día hábil. */
const Demo = () => {
  const [params] = useSearchParams();
  const initialPlan = PLANS.some((p) => p.value === params.get('plan')) ? params.get('plan') : 'performance_team';
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', plan: initialPlan, team_size: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (form.name.trim().length < 2) return toast.error('Escribe tu nombre');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error('Escribe un correo válido');
    setSending(true);
    try {
      await axios.post(`${API}/demo/request`, { ...form, phone: form.phone || null, organization: form.organization || null, team_size: form.team_size || null, message: form.message || null });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err, 'No se pudo enviar la solicitud'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-10 px-4" data-testid="demo-page">
      <div className="max-w-md mx-auto">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src="/brand/app-icon-square.webp" alt="ONOR CODE" className="h-12 w-12 rounded" />
          <span className="text-zinc-400 text-sm">Volver a la página principal</span>
        </Link>
        {done ? (
          <div className="rounded-sm border border-zinc-800 bg-zinc-900 p-6 text-center" data-testid="demo-done">
            <CheckCircle className="w-10 h-10 text-primary mx-auto mb-3" />
            <h1 className="font-display font-bold text-2xl text-white uppercase">Recibimos tu solicitud</h1>
            <p className="text-zinc-400 text-sm mt-2">Te contactamos en menos de un día hábil por correo o teléfono para agendar la demo.</p>
            <Link to="/" className="inline-block mt-6"><Button className="btn-primary px-6">Volver</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" noValidate>
            <div>
              <p className="text-primary text-xs uppercase tracking-wider font-bold">Equipos y coaches</p>
              <h1 className="font-display font-bold text-3xl text-white uppercase mt-1">Agenda una demo</h1>
              <p className="text-zinc-400 text-sm mt-2">Déjanos tus datos y te mostramos ONOR con tu caso real. Sin compromiso.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Nombre *</Label>
              <Input className="input-default" value={form.name} onChange={set('name')} placeholder="Tu nombre" autoComplete="name" data-testid="demo-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Correo *</Label>
              <Input type="email" inputMode="email" className="input-default" value={form.email} onChange={set('email')} placeholder="tu@correo.com" autoComplete="email" data-testid="demo-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Teléfono o WhatsApp (opcional)</Label>
              <Input type="tel" inputMode="tel" className="input-default" value={form.phone} onChange={set('phone')} placeholder="+52 …" autoComplete="tel" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Equipo, club u organización (opcional)</Label>
              <Input className="input-default" value={form.organization} onChange={set('organization')} placeholder="Ej: Halcones FC, Gimnasio Norte" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Me interesa</Label>
                <select className="input-default w-full h-11 px-3 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-100" value={form.plan} onChange={set('plan')} data-testid="demo-plan">
                  {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Personas a cargo</Label>
                <select className="input-default w-full h-11 px-3 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-100" value={form.team_size} onChange={set('team_size')}>
                  <option value="">Elegir…</option>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">¿Qué quieres resolver? (opcional)</Label>
              <Textarea className="input-default" rows={3} value={form.message} onChange={set('message')} placeholder="Ej: seguimiento de 15 atletas con planes distintos…" />
            </div>
            <Button type="submit" className="btn-primary w-full h-12" disabled={sending} data-testid="demo-submit">
              {sending ? 'Enviando…' : 'Solicitar demo'}
            </Button>
            <p className="text-zinc-500 text-xs text-center">Tus datos solo se usan para contactarte sobre la demo. <Link to="/privacidad" className="underline">Aviso de privacidad</Link>.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Demo;
